/**
 * Request a resized variant of a CDN image.
 *
 * The progress-data CloudFront distribution runs a Lambda@Edge resizer: `<url>?w=200` returns a
 * 200px-wide WebP (or JPEG for viewers that do not accept WebP) instead of the full-size original.
 * The resize happens once per (key, width, format) and is then served from S3/CloudFront, so the
 * cost of adding `?w=` at a call site is a one-off, not per-request.
 *
 * WHY THIS MATTERS
 * Measured on the admin template page: 30 images / 5.5 MB (~130 kB each) rendered as 56px
 * thumbnails — 56% of the page's bytes, and the last thing to finish loading, while all 33 API
 * calls together came to 39 kB. At `?w=160` those same images are ~2-4 kB each.
 *
 * WHY A HELPER AND NOT `` `${url}?w=200` ``
 * Appending the parameter blindly breaks in three ways this function exists to prevent:
 *
 *   1. Non-CDN URLs. `blob:`/`data:` previews of a file being uploaded, YouTube thumbnails and
 *      brand logos on other hosts are not behind the resizer. `?w=` is at best ignored and at
 *      worst breaks a signed URL.
 *   2. Widths the edge does not serve. The Lambda whitelists a fixed set of widths — every width
 *      is a separate cache entry, S3 object AND Lambda invocation, so an open parameter is a cost
 *      and a cache-pollution vector. An unrecognised width passes through at FULL size, silently
 *      undoing the optimisation.
 *   3. URLs that already carry a query string. `?w=` would produce a second `?`.
 *
 * In every case this returns the URL UNCHANGED, so the worst outcome is today's behaviour rather
 * than a broken <img>. That is also why it is safe to apply broadly.
 *
 * DPR / RETINA
 * Pass the CSS pixel size and let `dpr` do the doubling: a 56px avatar on a 2x screen wants 112px
 * of image, which snaps up to 160. Rendering a 56px-wide image into a 56px box on a retina display
 * looks soft.
 *
 * See progress-infra/modules/image-resizer for the edge functions themselves.
 */

/**
 * Widths the edge resizer will generate.
 *
 * MUST stay in sync with ALLOWED_WIDTHS in both progress-infra/modules/image-resizer/lambda/index.js
 * and .../lambda-request/index.js. A width missing from the edge list is not an error here — it
 * just serves the full-size original, which is why this list is the conservative one.
 */
export const ALLOWED_WIDTHS = [80, 160, 200, 320, 480, 640, 960] as const;

/** Hosts running the resizer. Anything else is returned untouched. */
const RESIZABLE_HOST = 'd3apbr8n1r4o20.cloudfront.net';

/**
 * Only objects under /images/ are resizable — the edge function's IAM policy grants GetObject on
 * `images/*` alone, so a `?w=` on any other prefix would fall through to the original anyway.
 */
const RESIZABLE_PATH = '/images/';

/**
 * Round a requested width UP to the nearest width the edge actually serves.
 *
 * Rounding UP rather than to the nearest keeps the image at least as large as the box it renders
 * into; rounding down would upscale in the browser and look blurry. Anything above the largest
 * allowed width returns null, meaning "serve the original" — the resizer never upscales, so asking
 * for 1600px of a 1000px image would return the original regardless.
 */
const snapWidth = (width: number): number | null => {
    for (const allowed of ALLOWED_WIDTHS) {
        if (width <= allowed) return allowed;
    }
    return null;
};

interface ImageUrlOptions {
    /**
     * Device pixel ratio to multiply the width by. Defaults to the current screen's, capped at 2 —
     * 3x displays exist but the extra bytes buy almost nothing perceptually at thumbnail sizes.
     */
    dpr?: number;
}

/**
 * Returns `url` with a `?w=` sized for `width` CSS pixels, or `url` unchanged when the resizer
 * cannot serve it (non-CDN host, non-image path, existing query string, or a width above the
 * largest variant).
 *
 * @param url    Image URL as it came from the API. Nullish passes through, so call sites do not
 *               need their own guard.
 * @param width  The size the image RENDERS at, in CSS pixels — not the size you want fetched.
 *               DPR scaling is applied on top.
 */
export const imageUrl = (
    url: string | null | undefined,
    width: number,
    {dpr}: ImageUrlOptions = {}
): string | null | undefined => {
    if (!url || typeof url !== 'string') return url;
    if (!Number.isFinite(width) || width <= 0) return url;

    // Cheap rejection before constructing a URL: blob:/data: previews are the common case at
    // upload-preview call sites and are never resizable.
    if (!url.includes(RESIZABLE_HOST)) return url;

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        // Relative or malformed — not something the resizer can serve.
        return url;
    }

    if (parsed.hostname !== RESIZABLE_HOST) return url;
    if (!parsed.pathname.startsWith(RESIZABLE_PATH)) return url;
    // A URL that already carries params (a signed URL, say) is left alone rather than guessing how
    // the extra parameter interacts with it.
    if (parsed.search) return url;

    const ratio = dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
    const snapped = snapWidth(Math.ceil(width * Math.min(ratio, 2)));
    if (snapped === null) return url;

    parsed.searchParams.set('w', String(snapped));
    return parsed.toString();
};

export default imageUrl;
